import "../../styles/global.css";
import "./Comparison.css"; // custom styles for this page
import React, { useState, useEffect, useMemo } from "react";
import { fetchQuestionAverageByStudent, fetchDemoQuestionAverageByStudent, 
         fetchClickSequenceByStudent, fetchDemoClickSequenceByStudent, fetchCurrentScoresByStudent} from "../../api/individualApi";
import { fetchQuestionAverage, fetchDemoQuestionAverage, 
         fetchAllSubmissionDates, fetchAllDemoSubmissionDates } from "../../api/overviewApi";
import { Slider, Typography, Select, MenuItem } from "@mui/material";
import dayjs from "dayjs";
import ClickBarChart from "./ClickBarChart";
import ScatterChart from "./ComparisonScatter"; 

// Mapping table
// const Construct_code_category = [
//   { construct: "Academic Burden", code: "bur", category: "Student Beliefs" },
//   { construct: "Withdrawal Intention", code: "with", category: "Learning Behaviors" },
//   { construct: "Self-Efficacy", code: "se", category: "Student Beliefs" },
//   { construct: "Value", code: "val", category: "Student Beliefs" },
//   { construct: "Intrinsic Motivation", code: "amot", category: "Student Beliefs" }, // or autonomous motivation? 
//   { construct: "Extrinsic Motivation", code: "cmot", category: "Student Beliefs" }, // or controlled motivation?
//   { construct: "Behavioral Engagement", code: "beh", category: "Learning Behaviors" },
//   { construct: "Cognitive Engagement", code: "cog", category: "Learning Behaviors" },
//   { construct: "Affective Engagement", code: "aff", category: "Learning Behaviors" },
//   { construct: "Self-Regulation", code: "srt", category: "Learning Behaviors" },
//   { construct: "Autonomy", code: "aut", category: "Classroom Context" },
//   { construct: "Relatedness", code: "rel", category: "Classroom Context" },
//   { construct: "Cultural Congruity", code: "cult", category: "Classroom Context" },
//   { construct: "Belongingness", code: "bel", category: "Classroom Context" }, // or perceived belonging?  
// ];

const Construct_code_category = [
    { construct: "Confidence", code: "con", category: "Student Learning" },
    { construct: "Study Habits", code: "sth", category: "Student Learning" },
    { construct: "Stress", code: "abur", category: "Student Learning" },
    { construct: "Motivation", code: "mot", category: "Student Learning" },
    { construct: "Resilience", code: "res", category: "Student Learning" }
];

export const studentAliasMap = {
  "Zoie Becker": "Neville Longbottom",
  "Eric Falcon Ledezma": "Luna Lovegood",
  "Tamiyah Hill": "Ginny Weasley",
  "Monica Pacheco": "Harry Potter",
  "Alec Plotegher": "Ron Weasley",
  "Wangtianjiao Cheng": "Hermione Granger",
  "Amya Johnson-Turpin": "Draco Malfoy",
};

const Comparison = () => {

    const [studentQuestionAverage, setStudentQuestionAverage] = useState([]);
    const [questionAverage, setQuestionAverage] = useState([]);
    const [error, setError] = useState(null);
    const [allDates, setAllDates] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [submissionRange, setSubmissionRange] = useState([null, null]);
    const [selectedCode, setSelectedCode] = useState("con")
    // const [clickSequence, setClickSequence] = useState({});
    const [studentClickStats, setStudentClickStats] = useState([]);
    const [selectedX, setSelectedX] = useState("con");
    const [selectedY, setSelectedY] = useState("sth");
    const [studentScores, setStudentScores] = useState([]); // array of { name, [code1]: val, [code2]: val }
    const [sessionInfo, setSessionInfo] = useState(null);
    const [currentScores, setCurrentScores] = useState([]);
    const [selectedSection, setSelectedSection] = useState("all");

    useEffect(() => {
        const cached = localStorage.getItem('iTOOLS_sessionInfo');
        if (cached) {
            console.log("Using cached session info");
            setSessionInfo(JSON.parse(cached));
        } else {
            console.log("No cached session info found");
            setSessionInfo({ name: "Demo Course", courseId: '93298' }); // Fallback to demo
        }
    }, []);

    useEffect(() => {
        if (!sessionInfo) return;
        console.log("Fetching all submission dates...");
        fetchAllSubmissionDates(sessionInfo?.courseId)
            .then((data) => {
                console.log("Comparison - Fetched all submission dates:", data);
                const raw = Array.isArray(data?.dates) ? data.dates : [];
                const ts = raw
                    .map((d) => Date.parse(d))
                    .filter((n) => Number.isFinite(n));
                // if (ts.length === 0) {
                //     // cold start: no submissions yet
                //     setAllDates([]);
                //     setDateRange([null, null]);
                //     return;
                // }
                const minDate = Math.min(...ts);
                const maxDate = Math.max(...ts);
                setSubmissionRange([minDate, maxDate]);
                

                const start_fa25 = Date.parse("2026-01-20T00:00:00-07:00");
                const end_now = Date.now();
                setDateRange([start_fa25, end_now]);
                setAllDates([start_fa25, end_now]);
            })
            .catch((error) => {
                console.error("Comparison - Error fetching all submission dates:", error);
                setAllDates([]);
                setDateRange([null, null]);
            });

        fetchCurrentScoresByStudent(sessionInfo?.courseId)
              .then((data) => {
                console.log("Comparison - Fetched current scores by student:", data.current_scores);
                setCurrentScores(data.current_scores || []);
              })
              .catch((error) => {
                console.error("Error fetching current scores by student:", error);
              });
    }, [sessionInfo]);

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            const [startISO, endISO] = dateRange.map((ts) => new Date(ts).toISOString());
            console.log("Comparison - Fetching data for date range:", startISO, endISO);
            fetchQuestionAverageByStudent(startISO, endISO, sessionInfo.courseId)
                .then((data) => setStudentQuestionAverage(data?.student_question_stats ?? [])) // not sure.. double check
                .catch(() => setError("Failed to load student averages"));

            fetchQuestionAverage(startISO, endISO, sessionInfo.courseId)
                .then((data) => setQuestionAverage(data.questions ?? []))
                .catch(() => setError("Failed to load class averages"))

            fetchClickSequenceByStudent(startISO, endISO, sessionInfo.courseId)
                .then((data) => setStudentClickStats(data?.click_sequence_stats ?? []))
                .catch(() => setError("Failed to load student click sequences"));


        }
    }, [dateRange, sessionInfo]);

    const sectionNumbers = useMemo(() => {
        const sections = [...new Set(
            studentQuestionAverage
                .map((d) => d.section_number)
                .filter(s => s != null)
        )].sort();
        return sections;
    }, [studentQuestionAverage]);

    const filteredSortedData = useMemo(() => {
        return studentQuestionAverage
            .filter((d) => d.group_code === selectedCode)
            .filter((d) => selectedSection === "all" || d.section_number === selectedSection)
            .sort((a, b) => a.avg_score - b.avg_score);
    }, [studentQuestionAverage, selectedCode, selectedSection])

    const pairedStudentScores = useMemo(() => {
        if (!selectedX || !selectedY || selectedX === selectedY) return [];
        const grouped = {};
        const isDemo = !sessionInfo?.courseId || String(sessionInfo?.courseId) === '93298';

        studentQuestionAverage
            .filter(d => selectedSection === "all" || d.section_number === selectedSection)
            .forEach(({ canvas_user_id, student_name, group_code, avg_score }) => {
                if (!grouped[canvas_user_id]) {
                    const alias = isDemo ? (studentAliasMap[student_name] ?? student_name) : student_name;
                    grouped[canvas_user_id] = { canvas_user_id, name: alias };
                }
                if (group_code === selectedX || group_code === selectedY) {
                    grouped[canvas_user_id][group_code] = avg_score;
                }
        });

        return Object.values(grouped).filter(
            d => d[selectedX] !== undefined && d[selectedY] !== undefined
        );
    }, [studentQuestionAverage, selectedX, selectedY, selectedSection, sessionInfo]);

    console.log("questionAverage", questionAverage);

    const pairedAvgScores = useMemo(() => {
        if (!selectedX || !selectedY || selectedX === selectedY) return null;

        const xEntry = questionAverage.find(q => q.group_code === selectedX);
        const yEntry = questionAverage.find(q => q.group_code === selectedY);

        if (xEntry && yEntry) {
            return {
                [selectedX]: xEntry.avg_score,
                [selectedY]: yEntry.avg_score
            };
        }
        return null; // return null if either entry is missing
    }, [questionAverage, selectedX, selectedY]);

    console.log("pairedAvgScores", pairedAvgScores);


    const classMeanScore = useMemo(() => {
        const sectionData = filteredSortedData; // already filtered by selectedCode + selectedSection
        if (sectionData.length === 0) return null;
        const sum = sectionData.reduce((acc, d) => acc + parseFloat(d.avg_score), 0);
        return sum / sectionData.length;
    }, [filteredSortedData]);

    console.log("classMeanScore", classMeanScore);

    // const studentNameMap = {};
    console.log("studentQuestionAverage", studentQuestionAverage);
    // studentQuestionAverage.forEach((d) => {
    //     studentNameMap[d.canvas_user_id] = d.student_name;
    // });

    const studentNameMap = {};
        currentScores.forEach((d) => {
            studentNameMap[d.canvas_user_id] = d.student_name;
    });

    const labeledStats = Object.entries(studentClickStats)
        .filter(([id]) => studentNameMap[id]) // Only keep those with a name
        .map(([id, stat]) => {
            const realName = studentNameMap[id];

            let alias;
            const isDemo = !sessionInfo?.courseId || sessionInfo?.courseId === 93298;
            console.log("isDemo", isDemo, "sessionInfo?.courseId", sessionInfo?.courseId, "realName", realName, "studentAliasMap[realName]", studentAliasMap[realName]);
            alias = isDemo ? (studentAliasMap[realName] ?? realName) : realName;

            return {
            canvas_user_id: id,
            realName,
            name: alias,
            ...stat
            };
        });
    
    const sectionFilteredLabeledStats = useMemo(() => {
        if (selectedSection === "all") return labeledStats;
        const sectionUserIds = new Set(
            studentQuestionAverage
                .filter((d) => d.section_number === selectedSection)
                .map((d) => String(d.canvas_user_id))  // ← add String() here
        );
        return labeledStats.filter(d => sectionUserIds.has(String(d.canvas_user_id)));
    }, [labeledStats, selectedSection, studentQuestionAverage]);    
    console.log("labeledStats", labeledStats);
    console.log("sectionFilteredLabeledStats", sectionFilteredLabeledStats);

    const handleCheckboxChange = (code) => {
        const isAlreadySelected = [selectedX, selectedY].includes(code);

        if (isAlreadySelected) {
            // Uncheck
            if (code === selectedX) setSelectedX("");
            if (code === selectedY) setSelectedY("");
        } else {
            // Add new selection
            if (!selectedX) {
            setSelectedX(code);
            } else if (!selectedY) {
            setSelectedY(code);
            } else {
            // both already filled, do nothing or alert user
            }
        }
    };
    

    const selectedConstruct = Construct_code_category.find((c) => c.code === selectedCode);

    const handleSliderChange = (event, newValue) => setDateRange(newValue);

    // console.log("filteredSortedData", filteredSortedData)

    return (
        <div className="wrapper">
            {/* <h1 className="title">COMPARISON OF STUDENT AVERAGES</h1> */}
            <div className="title-row" style={{ display: "flex", alignItems: "center", gap: "1.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                <h1 className="title" style={{ margin: 0 }}>COMPARISON OF STUDENT AVERAGES</h1>
                {sectionNumbers.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Typography variant="subtitle1"><strong>Filter by:</strong></Typography>
                        <Select
                            value={selectedSection ?? "all"}
                            onChange={(e) => setSelectedSection(e.target.value)}
                            style={{ minWidth: 160 }}
                            size="small"
                        >
                            <MenuItem value="all">All Sections</MenuItem>
                            {sectionNumbers.map((sec) => (
                                <MenuItem key={sec} value={sec}>Section {sec}</MenuItem>
                            ))}
                        </Select>
                    </div>
                )}
            </div>
            <div className="section-wrapper">
                <h2 className="section-title">1. Survey Data</h2>
                <p>Select a measure to show the rank of students from low to high. Drag the slider to adjust the time range of the surveys.</p>

                <div className="section-content">
                    {/* Left panel: controls */}
                    <div className="controls-panel">
                        <Typography variant="subtitle1" gutterBottom><strong>Select a measure:</strong></Typography>
                        <Select
                            value={selectedCode}
                            onChange={(e) => setSelectedCode(e.target.value)}
                            style={{ width: '100%', marginBottom: '2rem' }}
                        >
                            {Construct_code_category.map(({ construct, code}) => (
                                <MenuItem key={code} value={code}>{construct}</MenuItem>
                            ))}
                        </Select>

                        {/* {sectionNumbers.length > 0 && (
                            <>
                                <Typography variant="subtitle1" gutterBottom><strong>Filter by section:</strong></Typography>
                                <Select
                                    value={selectedSection}
                                    onChange={(e) => setSelectedSection(e.target.value)}
                                    style={{ width: '100%', marginBottom: '2rem' }}
                                >
                                    <MenuItem value="all">All Sections</MenuItem>
                                    {sectionNumbers.map((sec) => (
                                        <MenuItem key={sec} value={sec}>Section {sec}</MenuItem>
                                    ))}
                                </Select>
                            </>
                        )} */}

                        {Number.isFinite(allDates[0]) && Number.isFinite(allDates[1]) && 
                            Number.isFinite(dateRange[0]) && Number.isFinite(dateRange[1]) && (
                            <>
                                <Typography gutterBottom>Select time range of survey submissions and click data:</Typography>
                                <div className="slider-wrapper">
                                <Slider
                                    value={dateRange}
                                    onChange={handleSliderChange}
                                    valueLabelDisplay="auto"
                                    min={allDates[0]}
                                    max={allDates[1]}
                                    step={24 * 60 * 60 * 1000}
                                    valueLabelFormat={(ts) => dayjs(ts).format("MMM D")}
                                />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right panel: bar chart */}
                    <div className="bar-chart-wrapper">
                        <div className="label-column">
                            {/* {filteredSortedData.map((d) => {
                                // <div key={d.user_id} className="bar-label">
                                // {studentAliasMap[d.student_name] || d.student_name}
                                // </div>
                                const realName = d.student_name;
                                const alias = sessionInfo?.courseId
                                ? realName
                                : (studentAliasMap[realName] ?? realName);

                                return (
                                <div key={d.user_id} className="bar-label">
                                    {alias}
                                </div>
                                );
                            })}    */}
                            {filteredSortedData.map((d) => {
                                const realName = d.student_name;
                                const isDemo = !sessionInfo?.courseId || String(sessionInfo?.courseId) === "93298";
                                // console.log("isDemo", isDemo, "sessionInfo?.courseId", sessionInfo?.courseId, "realName", realName, "studentAliasMap[realName]", studentAliasMap[realName]);
                                const alias = isDemo ? (studentAliasMap[realName] ?? realName) : realName;
                                return (
                                    <div key={d.user_id} className="bar-label">{alias}</div>
                                );
                            })}
                        </div>
                        
                        <div className="bar-chart">
                            {classMeanScore && (
                                <>
                                <div
                                    className="class-mean-global-line"
                                    style={{ left: `${((classMeanScore - 1) / 5) * 100}%` }}
                                />
                                <div
                                    style={{
                                        position: "absolute",
                                        left: `${((classMeanScore - 1) / 5) * 100}%`,
                                        top: "-1.5rem",
                                        fontSize: "0.8rem",
                                        color: "#800080",
                                        transform: "translateX(-50%)"
                                    }}
                                    >
                                    {classMeanScore.toFixed(2)}
                                </div>
                                </>
                                
                            )}

                            {filteredSortedData.map((d) => (
                                <div key={d.user_id} className="bar-row">
                                    
                                    <div className="bar-container">
                                        <div
                                            className="bar-fill" 
                                            style={{ width: `${((parseFloat(d.avg_score) - 1) / 5) * 100}%` }} 
                                        /> 
                                        <div
                                            className="bar-dot"
                                            style={{ left: `${((parseFloat(d.avg_score) - 1) / 5) * 100}%` }}
                                        />
                                    </div>
                                    {/* <div className="bar-score">{parseFloat(d.avg_score).toFixed(2)}</div> */}
                                </div>
                            ))}
                            
                            {questionAverage.length > 0 ? (
                                <div className="x-axis">
                                    {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6].map((label) => (
                                        <div key={label} className="x-tick" style={{ left: `${((label - 1) / 5) * 100}%` }}>
                                            <div className="x-line" />
                                            <div className="x-label">
                                            {label === 1
                                                ? "Strongly Disagree"
                                                : label === 6
                                                ? "Strongly Agree"
                                                : label}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="muted-text">
                                    Survey data will populate once the quiz becomes available.
                                </p>
                            )}
                            
                        </div>
                    </div>
                </div>
                
            </div>

            <div className="section-wrapper">
                <h2 className="section-title">2. WebCampus Click Data</h2>
                <p>Students' click behaviors (what they click on in your course) are tracked using the following metrics. </p>
                <ul>
                    <li>
                        <strong>Click Coverage:</strong> Shows how much of the course the student has explored (e.g., assignments, quizzes, discussions). <u> Higher scores indicate broader exposure to course materials</u>.
                    </li>
                    <li>
                        <strong>Click Intensity:</strong> Reflects how actively the student engages with key course tools. <u>Higher scores indicate more frequent interaction with learning activities</u>.
                    </li>
                    <li>
                        <strong>Click Coherence:</strong>  Indicates whether the student tends to move through course materials in a logical, course-aligned sequence. <u> Higher scores suggest more organized and goal-directed navigation</u>.
                    </li>
                </ul>
                {sectionFilteredLabeledStats.length > 0 ? (
                    <ClickBarChart data={sectionFilteredLabeledStats} />
                ) : (
                    <p>No click data available.</p>
                )}
            </div>

            <div className="section-wrapper">
                <h2 className="section-title">3. Measure Comparison Matrix</h2>
                <p>Please choose any two measures to combine for comparison. This will allow you to search for students with combinations that may worthy of attention (e.g. high stress and low confidence). Hover over a dot to see the student's name and the actual scores. The red cross in the chart represents the class averages for the two selected measures. The scores are the average of all the survey responses up to this point.</p>
                
                <div className="section-content">
                    <div className="scatter-wrapper">
                        {questionAverage.length > 0 ? (
                                <ScatterChart
                                    individual={pairedStudentScores}
                                    avg={pairedAvgScores}
                                    xKey={selectedX}
                                    yKey={selectedY}
                                />
                        ) : (
                            <p className="muted-text">
                                Data will populate once the quiz becomes available.
                            </p>
                        )}
                    </div>
                    <div className="measure-checkboxes">
                        {Construct_code_category.map(({ construct, code }) => (
                            <label key={code} style={{ display: 'block', marginBottom: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    value={code}
                                    checked={[selectedX, selectedY].includes(code)}
                                    onChange={(e) => handleCheckboxChange(code)}
                                    disabled={
                                        ![selectedX, selectedY].includes(code) && selectedX && selectedY
                                    }
                                />
                                {' '}{construct}
                            </label>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Comparison;